import { Box, SxProps, Theme } from "@mui/material";
import type { JSX } from "react";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FormControlDatePicker } from "@/components/formControls/formControlDatePicker/FormControlDatePicker";
import { FormControlNumberInput } from "@/components/formControls/formControlNumberInput";
import { FormControlSelect } from "@/components/formControls/formControlSelect/FormControlSelect";
import { FormControlToggleButtonGroup, ToggleOption } from "@/components/formControls/formControlToggleButtonGroup";
import { useGetErrorMessage } from "@/hooks/useGetErrorMessage";
import {
  EnergyCertificateEnum,
  EnergyCertificateTypeEnum,
  EnergyEfficiencyHouseStandardEnum,
  ObjectTypeEnum,
} from "@/types/refineRealEstate";
import { PropertyInformation } from "@/types/refineRealEstate/refineRealEstate";

import {
  StyledFormGridContainer,
  StyledFormSection,
  StyledFormSectionHeader,
  StyledFormSectionTitle,
} from "../refineRealEstate.styled";

const toggleButtonGroupStyle: SxProps<Theme> = {
  gap: "4px",
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
};

interface EnergyAndPhotovoltaicTabProps {
  readonly?: boolean;
}

export const EnergyAndPhotovoltaicTab = ({ readonly }: EnergyAndPhotovoltaicTabProps): JSX.Element => {
  const { t } = useTranslation("Common", { keyPrefix: "realEstate" });

  const {
    control,
    formState: { errors },
    trigger,
    watch,
    setValue,
    clearErrors,
  } = useFormContext<PropertyInformation>();

  const getErrorMessage = useGetErrorMessage();

  const { generalInfo, energyAndPhotovoltaic } = watch();

  const availabilityOptions: ToggleOption<boolean>[] = [
    { value: true, label: t("available"), disabled: !!readonly },
    { value: false, label: t("notAvailable"), disabled: !!readonly },
  ];

  useEffect(() => {
    trigger();
  }, [trigger]);

  useEffect(() => {
    if (!energyAndPhotovoltaic.energyCertificate) {
      setValue("energyAndPhotovoltaic.issueDate", undefined, { shouldDirty: false });
      setValue("energyAndPhotovoltaic.certificateType", undefined, { shouldDirty: false });
      setValue("energyAndPhotovoltaic.finalEnergy", undefined, { shouldDirty: false });
      setValue("energyAndPhotovoltaic.primaryEnergy", undefined, { shouldDirty: false });
      setValue("energyAndPhotovoltaic.CO2Emissions", undefined, { shouldDirty: false });

      clearErrors([
        "energyAndPhotovoltaic.certificateType",
        "energyAndPhotovoltaic.finalEnergy",
        "energyAndPhotovoltaic.primaryEnergy",
        "energyAndPhotovoltaic.CO2Emissions",
      ]);
    } else {
      trigger("energyAndPhotovoltaic.certificateType");
    }
  }, [clearErrors, energyAndPhotovoltaic.energyCertificate, setValue, trigger]);

  useEffect(() => {
    if (energyAndPhotovoltaic.photovoltaicAvailable === true) {
      trigger(["energyAndPhotovoltaic.constructionYear", "energyAndPhotovoltaic.annualPowerYield"]);
    } else if (energyAndPhotovoltaic.photovoltaicAvailable === false) {
      setValue("energyAndPhotovoltaic.constructionYear", null, { shouldDirty: false, shouldValidate: false });
      setValue("energyAndPhotovoltaic.annualPowerYield", null, { shouldDirty: false, shouldValidate: false });
      clearErrors(["energyAndPhotovoltaic.constructionYear", "energyAndPhotovoltaic.annualPowerYield"]);
    }
  }, [energyAndPhotovoltaic.photovoltaicAvailable, clearErrors, setValue, trigger]);

  const energyCertificateOptions = Object.values(EnergyCertificateEnum).map((value) => ({
    value,
    label: t(`energyClass.values.${value}`),
  }));

  const energyEfficiencyHouseStandardOptions = Object.values(EnergyEfficiencyHouseStandardEnum).map((value) => ({
    value,
    label: t(`energyBuildingStandards.values.${value}`),
  }));

  type CertificateTypeTranslationKeys = "certificateType.values.demand" | "certificateType.values.consumption";

  const cardTypeOptions = Object.entries(EnergyCertificateTypeEnum).map(([key, value]) => ({
    value,
    label: t(`certificateType.values.${key}` as CertificateTypeTranslationKeys),
  }));

  const isDetailsDisabled = !energyAndPhotovoltaic.energyCertificate;
  const isHouse = generalInfo.objectType === ObjectTypeEnum.house;

  return (
    <StyledFormGridContainer>
      <StyledFormSection>
        <StyledFormSectionTitle>{t("energyClass.title")}</StyledFormSectionTitle>
        <FormControlSelect
          name="energyAndPhotovoltaic.energyCertificate"
          control={control}
          options={energyCertificateOptions}
          fullWidth
          selectSx={{ height: "45px" }}
          placeholder={t("pleaseSelect")}
          disabled={!!readonly}
          data-cy="refine-data-energy-certificate-class"
        />
      </StyledFormSection>
      <div />

      <StyledFormSection>
        <StyledFormSectionTitle>{t("issueDate")}</StyledFormSectionTitle>
        <FormControlDatePicker
          control={control}
          name="energyAndPhotovoltaic.issueDate"
          disabled={isDetailsDisabled || !!readonly}
          slotProps={{ field: { clearable: true } }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              height: "45px",
            },
          }}
          data-cy="refine-data-energy-certificate-issue-date"
        />
      </StyledFormSection>

      <StyledFormSection>
        <StyledFormSectionTitle>{t("certificateType.title")}</StyledFormSectionTitle>
        <FormControlSelect
          name="energyAndPhotovoltaic.certificateType"
          errorMsg={getErrorMessage(errors.energyAndPhotovoltaic?.certificateType)}
          control={control}
          options={cardTypeOptions}
          fullWidth
          selectSx={{ height: "45px" }}
          placeholder={t("pleaseSelect")}
          disabled={isDetailsDisabled || !!readonly}
          data-cy="refine-data-energy-certificate-type"
        />
      </StyledFormSection>

      <Box sx={{ opacity: isDetailsDisabled ? 0.5 : 1 }}>
        <StyledFormSection>
          <StyledFormSectionTitle>{t("finalEnergy")}</StyledFormSectionTitle>
          <FormControlNumberInput
            name="energyAndPhotovoltaic.finalEnergy"
            control={control}
            fullWidth
            error={!!errors.energyAndPhotovoltaic?.finalEnergy}
            helperText={getErrorMessage(errors.energyAndPhotovoltaic?.finalEnergy)}
            sx={{ height: "45px" }}
            disabled={isDetailsDisabled || !!readonly}
            data-cy="refine-data-energy-final"
          />
        </StyledFormSection>
      </Box>

      <Box sx={{ opacity: isDetailsDisabled ? 0.5 : 1 }}>
        <StyledFormSection>
          <StyledFormSectionTitle>{t("primaryEnergy")}</StyledFormSectionTitle>
          <FormControlNumberInput
            name="energyAndPhotovoltaic.primaryEnergy"
            control={control}
            fullWidth
            error={!!errors.energyAndPhotovoltaic?.primaryEnergy}
            helperText={getErrorMessage(errors.energyAndPhotovoltaic?.primaryEnergy)}
            sx={{ height: "45px" }}
            disabled={isDetailsDisabled || !!readonly}
            data-cy="refine-data-energy-primary"
          />
        </StyledFormSection>
      </Box>

      <Box sx={{ opacity: isDetailsDisabled ? 0.5 : 1 }}>
        <StyledFormSection>
          <StyledFormSectionTitle>{t("CO2Emissions")}</StyledFormSectionTitle>
          <FormControlNumberInput
            name="energyAndPhotovoltaic.CO2Emissions"
            control={control}
            fullWidth
            error={!!errors.energyAndPhotovoltaic?.CO2Emissions}
            helperText={getErrorMessage(errors.energyAndPhotovoltaic?.CO2Emissions)}
            disabled={isDetailsDisabled || !!readonly}
            sx={{ height: "45px" }}
            data-cy="refine-data-co2-emissions"
          />
        </StyledFormSection>
      </Box>

      <div />

      <StyledFormSection>
        <StyledFormSectionTitle>{t("energyBuildingStandards.title")}</StyledFormSectionTitle>
        <FormControlSelect
          name="energyAndPhotovoltaic.energyEfficiencyHouseStandard"
          control={control}
          options={energyEfficiencyHouseStandardOptions}
          fullWidth
          selectSx={{ height: "45px" }}
          placeholder={t("pleaseSelect")}
          disabled={!!readonly}
          data-cy="refine-data-energy-efficiency"
        />
      </StyledFormSection>

      {isHouse && (
        <>
          <StyledFormSectionHeader>{t("dataVerification.photovoltaicSystem")}</StyledFormSectionHeader>
          <Box sx={{ gridColumn: "span 2" }}>
            <FormControlToggleButtonGroup
              control={control}
              name="energyAndPhotovoltaic.photovoltaicAvailable"
              fullWidth
              sx={toggleButtonGroupStyle}
              options={availabilityOptions}
              data-cy="refine-data-photovoltaic-available"
            />
          </Box>

          {energyAndPhotovoltaic.photovoltaicAvailable && (
            <>
              <StyledFormSection>
                <StyledFormSectionTitle>{t("constructionYear")}</StyledFormSectionTitle>
                <FormControlNumberInput
                  name="energyAndPhotovoltaic.constructionYear"
                  control={control}
                  error={!!errors.energyAndPhotovoltaic?.constructionYear}
                  helperText={getErrorMessage(errors.energyAndPhotovoltaic?.constructionYear)}
                  disabled={!!readonly}
                  data-cy="refine-data-photovoltaic-construction-year"
                />
              </StyledFormSection>
              <StyledFormSection>
                <StyledFormSectionTitle>{t("annualPowerYield")}</StyledFormSectionTitle>
                <FormControlNumberInput
                  name="energyAndPhotovoltaic.annualPowerYield"
                  control={control}
                  error={!!errors.energyAndPhotovoltaic?.annualPowerYield}
                  helperText={getErrorMessage(errors.energyAndPhotovoltaic?.annualPowerYield)}
                  disabled={!!readonly}
                  data-cy="refine-data-photovoltaic-annual-power-yield"
                />
              </StyledFormSection>
            </>
          )}
        </>
      )}
    </StyledFormGridContainer>
  );
};
